import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  Farm, Block, CultivarArea,
  FarmFormData, BlockFormData, CultivarAreaFormData,
  FarmsResponse, BlocksResponse, CultivarAreasResponse,
  FarmMapData, FarmAssetSummary, GeoJSONPolygon, Pagination,
  PlantingPlan, PlantingPlanSaveData, PlantingPlanPreviewRequest, RowOverride, Pollinator,
  HarvestWindow, HarvestWindowFormData, HarvestWindowSuggestion
} from '../types/orchard';
import type { GrowingRegion } from '../types/cultivar';

// =============================================================================
// ORCHARD SERVICE
// =============================================================================

class OrchardService {

  // --- Farms ---

  async getFarms(page = 1, limit = 20, orgId?: number, includeInactive = false): Promise<ApiResponse<FarmsResponse>> {
    const org = orgId ? `&org_id=${orgId}` : '';
    const inactive = includeInactive ? '&include_inactive=true' : '';
    return apiService.get(`/orchard/farms?page=${page}&limit=${limit}${org}${inactive}`);
  }

  async getFarm(id: number, orgId?: number): Promise<ApiResponse<Farm>> {
    const qs = orgId ? `?org_id=${orgId}` : '';
    return apiService.get(`/orchard/farms/${id}${qs}`);
  }

  async createFarm(
    data: FarmFormData & { target_org_id?: number }
  ): Promise<ApiResponse<Farm>> {
    return apiService.post('/orchard/farms', data);
  }

  async updateFarm(id: number, data: Partial<FarmFormData>): Promise<ApiResponse<Farm>> {
    return apiService.put(`/orchard/farms/${id}`, data);
  }

  async deleteFarm(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/orchard/farms/${id}`);
  }

  async getFarmAssetSummary(farmId: number): Promise<ApiResponse<FarmAssetSummary>> {
    return apiService.get(`/orchard/farms/${farmId}/asset-summary`);
  }

  async getFarmMapData(farmId: number, orgId?: number): Promise<ApiResponse<FarmMapData>> {
    const qs = orgId ? `?org_id=${orgId}` : '';
    return apiService.get(`/orchard/farms/${farmId}/map-data${qs}`);
  }

  // --- Growing regions, for farms ---
  // The same regions as /cultivar/growing-regions, without needing a cultivar
  // seat: a farm's region is a property of the farm.

  async listGrowingRegions(): Promise<ApiResponse<GrowingRegion[]>> {
    return apiService.get('/orchard/growing-regions');
  }

  async createGrowingRegion(data: Partial<GrowingRegion> & { name: string }): Promise<ApiResponse<GrowingRegion>> {
    return apiService.post('/orchard/growing-regions', data);
  }

  async findNearestRegion(latitude: number, longitude: number): Promise<ApiResponse<{ region: GrowingRegion; distance_km: number } | null>> {
    return apiService.get(`/orchard/growing-regions/nearest?latitude=${latitude}&longitude=${longitude}`);
  }

  /** The region for a farm at this address — the nearest close enough, else one made from the address. */
  async resolveGrowingRegion(data: {
    latitude: number; longitude: number;
    area?: string | null; locality?: string | null; state?: string | null;
    country?: string | null; country_code?: string | null;
  }): Promise<ApiResponse<{ region: GrowingRegion; created: boolean; distance_km: number | null } | null>> {
    return apiService.post('/orchard/growing-regions/resolve', data);
  }

  // --- Blocks ---

  async getBlocks(farmId?: number, page = 1, limit = 20): Promise<ApiResponse<Block[] | BlocksResponse>> {
    if (farmId) {
      // Get blocks for specific farm (returns array)
      return apiService.get(`/orchard/blocks?farm_id=${farmId}`);
    } else {
      // Get all blocks with pagination
      return apiService.get(`/orchard/blocks?page=${page}&limit=${limit}`);
    }
  }

  async getBlock(id: number, orgId?: number): Promise<ApiResponse<Block>> {
    const qs = orgId ? `?org_id=${orgId}` : '';
    return apiService.get(`/orchard/blocks/${id}${qs}`);
  }

  async createBlock(
    data: BlockFormData & { target_org_id?: number }
  ): Promise<ApiResponse<Block>> {
    return apiService.post('/orchard/blocks', data);
  }

  async updateBlock(id: number, data: Partial<BlockFormData>): Promise<ApiResponse<Block>> {
    return apiService.put(`/orchard/blocks/${id}`, data);
  }

  async deleteBlock(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/orchard/blocks/${id}`);
  }

  // --- Cultivar Areas ---

  async getCultivarAreas(filters?: {
    block_id?: number;
    farm_id?: number;
    cultivar_id?: number;
    page?: number;
    limit?: number;
    org_id?: number;
  }): Promise<ApiResponse<CultivarArea[] | CultivarAreasResponse>> {
    const queryParams = new URLSearchParams();

    if (filters?.block_id) {
      queryParams.append('block_id', String(filters.block_id));
      if (filters.org_id) queryParams.append('org_id', String(filters.org_id));
      return apiService.get(`/orchard/cultivar-areas?${queryParams.toString()}`);
    }

    if (filters?.farm_id) queryParams.append('farm_id', String(filters.farm_id));
    if (filters?.cultivar_id) queryParams.append('cultivar_id', String(filters.cultivar_id));
    if (filters?.page) queryParams.append('page', String(filters.page));
    if (filters?.limit) queryParams.append('limit', String(filters.limit));
    if (filters?.org_id) queryParams.append('org_id', String(filters.org_id));

    const qs = queryParams.toString();
    return apiService.get(`/orchard/cultivar-areas${qs ? `?${qs}` : ''}`);
  }

  // --- Whole-org lists ---
  //
  // The list endpoints page at 20 by default, which silently truncated every
  // picker that called them bare: an org's 21st cultivar area could not be
  // targeted, and it dropped out of the spray's total area. These walk every
  // page and return the same `{ farms | blocks | areas }` shape.

  private async fetchAllPages<K extends 'farms' | 'blocks' | 'areas', T>(
    key: K,
    fetchPage: (page: number) => Promise<ApiResponse<unknown>>,
  ): Promise<ApiResponse<Record<K, T[]>>> {
    const PAGE_LIMIT = 200;
    const rows: T[] = [];
    for (let page = 1; ; page++) {
      const res = await fetchPage(page);
      if (!res.success) return res as ApiResponse<Record<K, T[]>>;
      const data = res.data as (Record<K, T[]> & { pagination?: Pagination }) | null;
      rows.push(...(data?.[key] ?? []));
      const pages = data?.pagination?.pages ?? 1;
      if (page >= pages || (data?.[key]?.length ?? 0) < PAGE_LIMIT) break;
    }
    return { success: true, data: { [key]: rows } as Record<K, T[]> };
  }

  async getAllFarms(): Promise<ApiResponse<{ farms: Farm[] }>> {
    return this.fetchAllPages<'farms', Farm>('farms', page => this.getFarms(page, 200));
  }

  async getAllBlocks(): Promise<ApiResponse<{ blocks: Block[] }>> {
    return this.fetchAllPages<'blocks', Block>('blocks', page => this.getBlocks(undefined, page, 200));
  }

  async getAllCultivarAreas(): Promise<ApiResponse<{ areas: CultivarArea[] }>> {
    return this.fetchAllPages<'areas', CultivarArea>('areas', page => this.getCultivarAreas({ page, limit: 200 }));
  }

  async getCultivarArea(id: number, orgId?: number): Promise<ApiResponse<CultivarArea>> {
    const qs = orgId ? `?org_id=${orgId}` : '';
    return apiService.get(`/orchard/cultivar-areas/${id}${qs}`);
  }

  async createCultivarArea(
    data: CultivarAreaFormData & { target_org_id?: number }
  ): Promise<ApiResponse<CultivarArea>> {
    return apiService.post('/orchard/cultivar-areas', data);
  }

  async updateCultivarArea(id: number, data: Partial<CultivarAreaFormData>): Promise<ApiResponse<CultivarArea>> {
    return apiService.put(`/orchard/cultivar-areas/${id}`, data);
  }

  async deleteCultivarArea(id: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/orchard/cultivar-areas/${id}`);
  }

  // --- Harvest Windows ---

  async getHarvestWindows(areaId: number): Promise<ApiResponse<HarvestWindow[]>> {
    return apiService.get(`/orchard/cultivar-areas/${areaId}/harvest-windows`);
  }

  /** Creates the season's window, or replaces it where one already exists. */
  async saveHarvestWindow(
    areaId: number,
    data: HarvestWindowFormData
  ): Promise<ApiResponse<HarvestWindow>> {
    return apiService.post(`/orchard/cultivar-areas/${areaId}/harvest-windows`, data);
  }

  async deleteHarvestWindow(windowId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/orchard/harvest-windows/${windowId}`);
  }

  /** Dates proposed from the cultivar's maturity history. Saves nothing. */
  async suggestHarvestWindow(
    areaId: number,
    seasonYear: number
  ): Promise<ApiResponse<HarvestWindowSuggestion | null>> {
    return apiService.get(
      `/orchard/cultivar-areas/${areaId}/harvest-windows/suggest?season_year=${seasonYear}`
    );
  }

  // --- Planting Plan ---

  async getPlantingPlan(areaId: number): Promise<ApiResponse<PlantingPlan>> {
    return apiService.get(`/orchard/cultivar-areas/${areaId}/planting-plan`);
  }

  async savePlantingPlan(
    areaId: number,
    data: PlantingPlanSaveData
  ): Promise<ApiResponse<PlantingPlan>> {
    return apiService.put(`/orchard/cultivar-areas/${areaId}/planting-plan`, data);
  }

  /** Lay out a plan without saving it — for a planting still being drawn, or
   *  edits not yet saved. The row geometry lives server-side only. */
  async previewPlantingPlan(data: PlantingPlanPreviewRequest): Promise<ApiResponse<PlantingPlan>> {
    return apiService.post('/orchard/planting-plan/preview', data);
  }

  async upsertRowOverride(
    areaId: number,
    rowNumber: number,
    data: {
      primary_cultivar_id?: number | null;
      pollinators?: Pollinator[] | null;
      notes?: string | null;
    }
  ): Promise<ApiResponse<RowOverride>> {
    return apiService.put(`/orchard/cultivar-areas/${areaId}/rows/${rowNumber}`, data);
  }

  async deleteRowOverride(areaId: number, rowNumber: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/orchard/cultivar-areas/${areaId}/rows/${rowNumber}`);
  }

  // --- Utility Methods ---

  async calculateArea(geometry: GeoJSONPolygon): Promise<ApiResponse<{ hectares: number }>> {
    return apiService.post('/orchard/calculate-area', { geometry });
  }

  async searchCultivars(
    search: string,
    limit = 10,
    cultivarRole?: 'scion' | 'rootstock'
  ): Promise<ApiResponse<{ cultivars: { id: number; name: string; trade_name: string | null; crop_type_name: string | null; protection_status?: string | null }[] }>> {
    const params = new URLSearchParams({ search, limit: String(limit) });
    if (cultivarRole) {
      params.append('cultivar_role', cultivarRole);
    }
    const qs = params.toString();
    return apiService.get(`/orchard/cultivars/search?${qs}`);
  }

  // Client-side utility: Calculate tree count from area and spacing
  calculateTreeCount(areaHectares: number, rowSpacingMeters: number, treeSpacingMeters: number): number {
    const areaSqMeters = areaHectares * 10000;
    const treeAreaSqMeters = rowSpacingMeters * treeSpacingMeters;
    const theoreticalCount = areaSqMeters / treeAreaSqMeters;
    const efficiencyFactor = 0.90; // 90% efficiency
    return Math.round(theoreticalCount * efficiencyFactor);
  }

  // Client-side utility: Calculate center point from polygon coordinates
  calculateCenterPoint(coordinates: number[][][]): { lat: number; lng: number } {
    const polygon = coordinates[0];
    if (!polygon || polygon.length === 0) return { lat: 0, lng: 0 };

    let sumLat = 0;
    let sumLng = 0;
    const count = polygon.length - 1; // Exclude last point if duplicate of first

    for (let i = 0; i < count; i++) {
      sumLng += polygon[i][0];
      sumLat += polygon[i][1];
    }

    return {
      lat: Math.round((sumLat / count) * 1000000) / 1000000, // 6 decimal places
      lng: Math.round((sumLng / count) * 1000000) / 1000000
    };
  }
}

export default new OrchardService();
